import { Button } from "@repo/ui/components/Base/Button";
import { Label } from "@repo/ui/components/Base/Label";
import { PlusIcon } from "lucide-react";
import {
  DataTable,
  type TableLayoutState,
} from "@repo/ui/components/Base/DataTable";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { cn } from "@repo/shared/utils";
import { memo } from "react";
import { useUserColumns } from "@/hooks/useUserColumns";

export interface SearchableTableProps<T> {
  onCreate: () => void;
  showCreate?: boolean;
  label: string;
  subLabel?: string;
  createLabel?: string;
  columns: ColumnDef<T>[];
  onRowClick?: (row: Row<T>) => void;
  className?: string;
  data: T[];
  savedLayoutKey: string;
  layoutDefaults?: TableLayoutState;
}

const RawSearchableTable = <T extends { id: string }>({
  onCreate,
  showCreate = true,
  label,
  subLabel,
  createLabel,
  columns,
  onRowClick,
  className,
  data,
  savedLayoutKey,
  layoutDefaults,
}: SearchableTableProps<T>) => {
  const { defaultState, saveTableLayout, resetTableLayout } = useUserColumns(
    savedLayoutKey,
    layoutDefaults
  );

  return (
    <div
      className={cn(
        "flex flex-col bg-background w-full border p-4 shadow-lg rounded-lg",
        className
      )}
    >
      <div className="flex flex-row items-end">
        <Label label={label} />
        {subLabel && <div className="text-xs italic">{subLabel}</div>}
        <div className="flex-grow" />
        {showCreate && (
          <Button variant="tertiary" type="button" onClick={onCreate} size="sm">
            <PlusIcon className="mr-4" />
            {createLabel}
          </Button>
        )}
      </div>
      <div className="flex flex-row justify-between items-center">
        <div className="w-0 flex-grow h-[500px] overflow-auto gap-2 flex flex-col p-4 border-t mt-4">
          <DataTable
            columns={columns}
            data={data ?? []}
            hasTags
            onRowClick={onRowClick}
            userDefaults={defaultState}
            factoryDefaults={layoutDefaults}
            onLayoutSave={saveTableLayout}
            onLayoutReset={resetTableLayout}
          />
        </div>
      </div>
    </div>
  );
};

export const SearchableTable = memo(
  RawSearchableTable
) as typeof RawSearchableTable;
